import { makeAutoObservable, runInAction } from 'mobx'
import { notification } from 'antd'
import { openApi } from '../../helpers/request'
import { storeSession } from '../../helpers/stores'
import i from '../../translations/i'

// Поля, по которым ищет единая строка поиска
export type ProductSearchField = 'name' | 'id' | 'id_crm' | 'vendor_code'
export const PRODUCT_SEARCH_FIELDS: ProductSearchField[] = ['name', 'id', 'id_crm', 'vendor_code']

const SEARCH_FILTER: Record<ProductSearchField, string> = {
    name: 'filter[name]',
    id: 'filter[product_id][]',
    id_crm: 'filter[id_crm]',
    vendor_code: 'filter[vendor_code]',
}

// Сортировка по колонкам: ключи, которые понимает бэкенд (QueryList, sorting[][key])
export type ProductSortKey = 'name' | 'price' | 'count'
export const PRODUCT_SORT_KEYS: ProductSortKey[] = ['name', 'price', 'count']
export interface ProductSort {
    key: ProductSortKey
    order: 'asc' | 'desc'
}

export type ProductStatus = 'all' | 'active' | 'hidden'

// Ручной порядок товаров в категории (POST products/direction)
export type ProductMove = 'up' | 'down' | 'all_up'

// category_id = "0" — товары без категории
export const WITHOUT_CATEGORY = '0'

const DEFAULT_LIMIT = 10
const SEARCH_DELAY = 500

export interface ProductTableOptions {
    // /products — поиск по всем товарам; /products/list — товары категории в её порядке, с хлебными крошками;
    // /products/select — показанные в каталоге товары с вариациями и их свойствами (добавление в заказ)
    endpoint?: '/products' | '/products/list' | '/products/select'
    // Категория экрана: фильтр категории не показывается. WITHOUT_CATEGORY — товары без категории
    categoryId?: string | null
    important?: boolean
    // Статус при открытии и после сброса фильтров: выбор товаров в модалках начинается с показанных в каталоге
    status?: ProductStatus
    // Дополнительные параметры запроса списка, например values_categories=1
    query?: string[]
    // Фильтры, сортировка и страница — в адресе: после перехода в товар и обратно список открывается там же
    syncUrl?: boolean
    // Весь ответ списка — например, хлебные крошки категории
    onLoaded?: (result: any) => void
    // Панель — по тому, что умеет эндпоинт: поля поиска (по умолчанию все), фильтры категории, склада и статуса
    // и сортировка по колонкам (по умолчанию есть). /products/select ищет по названию, ID и ID CRM и только
    searchFields?: ProductSearchField[]
    filters?: boolean
    sortable?: boolean
}

// /products/select отдаёт вариации без единицы — берём её из справочника единиц проекта, как карточка заказа
const withProjectUnits = (list: any[]) => {
    const units: any[] = storeSession.get('project_units') ?? []
    list.forEach(product => (product.children ?? []).forEach((variant: any) => {
        if (variant.unit === undefined) variant.unit = units.find(unit => unit.id === variant.unit_id) ?? null
    }))
}

// Состояние одной таблицы товаров (ProductTable). У каждого экрана свой экземпляр
export class ProductTableController {

    readonly options: ProductTableOptions

    list: any[] = []
    loading = false
    total = 0
    page = 1
    limit = DEFAULT_LIMIT

    searchField: ProductSearchField = 'name'
    searchText = ''
    categoryId: string | null = null
    warehouseId: number | null = null
    status: ProductStatus = 'all'
    sort: ProductSort | null = null

    categoryTree: any[] = []

    // Выбор строк — только среди товаров текущей страницы
    selectedIds: number[] = []
    bulkLoading = false
    // Товары, у которых сейчас переключается показ в каталоге или меняется место
    busyIds: number[] = []
    // Товары с раскрытыми вариациями (вариации приходят в children)
    expandedIds: number[] = []

    private searchTimeout: ReturnType<typeof setTimeout> | null = null

    constructor(options: ProductTableOptions = {}) {
        this.options = { endpoint: '/products', ...options }
        this.status = this.defaultStatus
        this.searchField = this.searchFields[0]
        makeAutoObservable<ProductTableController, 'searchTimeout'>(this, { options: false, searchTimeout: false })
    }

    // Категория задана экраном (каталог, «без категории») — фильтровать по ней нельзя
    get fixedCategory(): boolean {
        return this.options.categoryId !== undefined && this.options.categoryId !== null
    }

    get defaultStatus(): ProductStatus {
        return this.options.status ?? 'all'
    }

    get searchFields(): ProductSearchField[] {
        return this.options.searchFields?.length ? this.options.searchFields : PRODUCT_SEARCH_FIELDS
    }

    get hasFilters(): boolean {
        return this.options.filters ?? true
    }

    get sortable(): boolean {
        return this.options.sortable ?? true
    }

    get filtered(): boolean {
        return !!(this.searchText || (!this.fixedCategory && this.categoryId) || this.warehouseId || this.status !== this.defaultStatus)
    }

    // Ручной порядок есть только у товаров одной категории и только без сортировки по колонке
    get canMove(): boolean {
        return this.fixedCategory && this.options.categoryId !== WITHOUT_CATEGORY && !this.sort
    }

    load() {
        if (this.options.syncUrl) this.readUrl()
        if (this.hasFilters && !this.fixedCategory) this.getCategoryTree()
        this.getList()
    }

    getList(page: number | null = null) {
        this.cancelSearch()

        if (page) this.page = page

        const params = [
            `limit=${this.limit}`,
            `page=${this.page}`,
            'with[]=image',
            'with[]=unit',
        ]

        const categoryId = this.fixedCategory ? this.options.categoryId : this.categoryId
        if (categoryId !== null && categoryId !== undefined) params.push(`filter[category_id]=${categoryId}`)
        if (this.searchText) params.push(`${SEARCH_FILTER[this.searchField]}=${encodeURIComponent(this.searchText)}`)
        if (this.status === 'active') params.push('viewed=1')
        if (this.status === 'hidden') params.push('viewed=0')
        if (this.warehouseId) params.push(`cloud_warehouse_id=${this.warehouseId}`)
        if (this.sort) params.push(`sorting[0][key]=${this.sort.key}`, `sorting[0][order]=${this.sort.order}`)
        if (this.options.important) params.push('important=true')
        if (this.options.query) params.push(...this.options.query)

        this.loading = true

        openApi('get', `${this.options.endpoint}?${params.join('&')}`)
            .then(result => {
                const list = result.data ?? []
                if (this.options.endpoint === '/products/select') withProjectUnits(list)

                runInAction(() => {
                    this.list = list
                    this.total = result.total ?? 0
                })
                this.options.onLoaded?.(result)
            })
            .catch(error => console.log(error?.response))
            .finally(() => runInAction(() => this.loading = false))
    }

    // ---------- Фильтры ----------

    // Поиск — с задержкой, чтобы не дёргать API на каждый символ
    setSearch(text: string) {
        this.searchText = text
        this.selectedIds = []
        this.cancelSearch()
        this.searchTimeout = setTimeout(() => this.getList(1), SEARCH_DELAY)
    }

    setSearchField(field: ProductSearchField) {
        this.searchField = field
        if (this.searchText) this.reload()
    }

    setCategory(categoryId: string | number | null | undefined) {
        this.categoryId = categoryId === null || categoryId === undefined ? null : `${categoryId}`
        this.reload()
    }

    setWarehouse(warehouseId: number | null | undefined) {
        this.warehouseId = warehouseId ?? null
        this.reload()
    }

    setStatus(status: ProductStatus) {
        this.status = status
        this.reload()
    }

    // Таблица отдаёт страницу, размер страницы и сортировку одним событием
    setTableState(page: number, limit: number, sort: ProductSort | null) {
        const sortChanged = sort?.key !== this.sort?.key || sort?.order !== this.sort?.order

        this.sort = sort

        if (limit !== this.limit || sortChanged) {
            this.limit = limit
            this.reload()
        } else if (page !== this.page) {
            this.selectedIds = []
            this.getList(page)
        }
    }

    resetFilters() {
        this.searchText = ''
        this.categoryId = null
        this.warehouseId = null
        this.status = this.defaultStatus
        this.reload()
    }

    private reload() {
        this.selectedIds = []
        this.getList(1)
    }

    private cancelSearch() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = null
        }
    }

    getCategoryTree() {
        openApi('get', '/category/tree')
            .then(result => runInAction(() => this.categoryTree = result.data ?? []))
            .catch(error => console.log(error?.response))
    }

    // ---------- Действия ----------

    toggleExpanded(id: number) {
        this.expandedIds = this.expandedIds.includes(id)
            ? this.expandedIds.filter(expandedId => expandedId !== id)
            : [...this.expandedIds, id]
    }

    // Товар страницы или его вариация
    private findProduct(id: number): any {
        for (const product of this.list) {
            if (product.id === id) return product
            const variant = (product.children ?? []).find((child: any) => child.id === id)
            if (variant) return variant
        }
        return undefined
    }

    isBusy(id: number) {
        return this.busyIds.includes(id)
    }

    private setBusy(id: number, busy: boolean) {
        this.busyIds = busy ? [...this.busyIds, id] : this.busyIds.filter(busyId => busyId !== id)
    }

    // Показ в каталоге: переключатель меняется сразу; при ошибке возвращается как был
    setActive(id: number, view: boolean) {
        const product = this.findProduct(id)
        const previous = product?.view
        if (product) product.view = +view

        this.setBusy(id, true)

        this.requestActive(id, view)
            .then(() => this.getList())
            .catch(() => runInAction(() => {
                if (product) product.view = previous
                notification.error({ message: i.t('def:errors.error'), description: i.t('def:errors.somethingWentWrong') })
            }))
            .finally(() => runInAction(() => this.setBusy(id, false)))
    }

    private requestActive(id: number, view: boolean) {
        const data = new FormData()
        data.append('view', `${+ view}`)
        return openApi('post', `/shop/product/active/${id}`, data)
    }

    move(id: number, action: ProductMove) {
        if (!this.canMove) return

        this.setBusy(id, true)

        openApi('post', `/products/direction/${id}/${this.options.categoryId}/${action}`)
            .then(() => this.getList())
            .catch(() => notification.error({ message: i.t('def:errors.error'), description: i.t('def:errors.somethingWentWrong') }))
            .finally(() => runInAction(() => this.setBusy(id, false)))
    }

    setSelectedIds(ids: number[]) {
        this.selectedIds = ids
    }

    // Массово показать или скрыть: у API переключение только по одному товару
    setActiveSelected(view: boolean) {
        const ids = [...this.selectedIds]
        if (!ids.length) return

        this.bulkLoading = true

        Promise.allSettled(ids.map(id => this.requestActive(id, view)))
            .then(results => {
                const failed = results.filter(result => result.status === 'rejected').length

                if (failed) {
                    notification.error({ message: i.t('def:errors.error'), description: i.t('productCards:bulkFailed', { count: failed }) })
                } else {
                    notification.success({
                        message: i.t('def:success.complete'),
                        description: i.t(view ? 'productCards:bulkShown' : 'productCards:bulkHidden', { count: ids.length }),
                    })
                }

                runInAction(() => this.selectedIds = [])
                this.getList()
            })
            .finally(() => runInAction(() => this.bulkLoading = false))
    }

    deleteSelected() {
        const ids = [...this.selectedIds]
        if (!ids.length) return

        const data = new FormData()
        ids.forEach(id => data.append('elements[]', `${id}`))

        this.bulkLoading = true

        openApi('post', '/shop/product/delete', data)
            .then(() => {
                runInAction(() => this.selectedIds = [])
                this.getList()
            })
            .catch(() => notification.error({ message: i.t('def:errors.error'), description: i.t('def:errors.somethingWentWrong') }))
            .finally(() => runInAction(() => this.bulkLoading = false))
    }

    // ---------- Адрес страницы ----------

    get url(): string {
        const params = new URLSearchParams()

        if (this.searchText) {
            params.set('q', this.searchText)
            if (this.searchField !== 'name') params.set('by', this.searchField)
        }
        if (!this.fixedCategory && this.categoryId) params.set('category', this.categoryId)
        if (this.warehouseId) params.set('warehouse', `${this.warehouseId}`)
        if (this.status !== 'all') params.set('status', this.status)
        if (this.sort) params.set('sort', `${this.sort.key}.${this.sort.order}`)
        if (this.page > 1) params.set('page', `${this.page}`)
        if (this.limit !== DEFAULT_LIMIT) params.set('limit', `${this.limit}`)

        const query = params.toString()
        return `${window.location.pathname}${query ? `?${query}` : ''}`
    }

    private readUrl() {
        const params = new URLSearchParams(window.location.search)

        const by = params.get('by') as ProductSearchField | null
        this.searchField = by && this.searchFields.includes(by) ? by : this.searchFields[0]
        this.searchText = params.get('q') ?? ''

        if (!this.fixedCategory) this.categoryId = params.get('category')
        this.warehouseId = Number(params.get('warehouse')) || null

        const status = params.get('status')
        this.status = status === 'active' || status === 'hidden' ? status : 'all'

        const [sortKey, sortOrder] = (params.get('sort') ?? '').split('.')
        this.sort = PRODUCT_SORT_KEYS.includes(sortKey as ProductSortKey) && (sortOrder === 'asc' || sortOrder === 'desc')
            ? { key: sortKey as ProductSortKey, order: sortOrder }
            : null

        this.page = Number(params.get('page')) || 1
        this.limit = Number(params.get('limit')) || DEFAULT_LIMIT
    }
}
